
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { fetchUserBookings, cancelBooking } from '@/lib/supabase';
import { BookingType } from '@/lib/supabase/types';
import { toZonedTime } from 'date-fns-tz';
import { parseISO, differenceInHours } from 'date-fns';

export const useBookings = (userId: string | undefined) => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<BookingType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [classIdToCancel, setClassIdToCancel] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const ARMENIA_TIMEZONE = 'Asia/Yerevan';
  
  useEffect(() => {
    if (userId) {
      fetchBookings(userId);
    }
  }, [userId]);
  
  const fetchBookings = async (userId: string) => {
    if (!userId) {
      console.error("[useBookings] No user ID provided for fetching bookings");
      return;
    }
    
    try {
      setIsLoading(true);
      console.log("[useBookings] Fetching bookings for user:", userId);
      const userBookings = await fetchUserBookings(userId);
      setBookings(userBookings);
    } catch (error) {
      console.error("[useBookings] Error fetching bookings:", error);
      toast.error(t("dashboard.bookings.loadError"), {
        description: t("dashboard.bookings.tryAgain"),
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const isBookingPast = (booking: BookingType): boolean => {
    if (!booking.classDate || !booking.classTime) return false;
    
    try {
      const datePart = parseISO(booking.classDate);
      
      let hours = 0;
      let minutes = 0;
      const timeMatch = booking.classTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      
      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = parseInt(timeMatch[2], 10);
        
        if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) {
          hours += 12;
        }
        if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) {
          hours = 0;
        }
      }
      
      const classDateObj = new Date(datePart);
      classDateObj.setHours(hours, minutes, 0, 0);
      
      const classDateAMT = toZonedTime(classDateObj, ARMENIA_TIMEZONE);
      const nowAMT = toZonedTime(new Date(), ARMENIA_TIMEZONE);
      
      const classEndTime = new Date(classDateAMT);
      classEndTime.setHours(classEndTime.getHours() + 3);
      
      return nowAMT > classEndTime;
    } catch (error) {
      console.error('[useBookings] Error checking if booking is past:', error);
      return false;
    }
  };
  
  const canCancelBooking = (booking: BookingType): boolean => {
    if (!booking.classDate || booking.classDate.length === 0) {
      return false; // Can't determine if within 24 hours
    }
    
    try {
      const parsedDate = parseISO(booking.classDate);
      
      let hours = 0;
      let minutes = 0;
      
      if (booking.classTime) {
        const timeMatch = booking.classTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeMatch) {
          hours = parseInt(timeMatch[1], 10);
          minutes = parseInt(timeMatch[2], 10);
          
          if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) {
            hours += 12;
          }
          if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
          }
        }
      }
      
      const classDateTime = new Date(parsedDate);
      classDateTime.setHours(hours, minutes, 0, 0);
      
      const classDateAMT = toZonedTime(classDateTime, ARMENIA_TIMEZONE);
      const nowAMT = toZonedTime(new Date(), ARMENIA_TIMEZONE);
      
      const hoursLeft = differenceInHours(classDateAMT, nowAMT);
      console.log(`Class time: ${classDateAMT}, Now: ${nowAMT}, Hours left: ${hoursLeft}`);
      
      return hoursLeft > 24;
    } catch (error) {
      console.error("[useBookings] Error calculating cancellation eligibility:", error);
      return false;
    }
  };
  
  const handleCancelBooking = (bookingId: string, classId: string) => {
    setBookingToCancel(bookingId);
    setClassIdToCancel(classId);
  };
  
  const handleConfirmCancel = async () => {
    if (!bookingToCancel || !classIdToCancel) return;
    
    setIsCancelling(true);
    
    try {
      toast.loading(t("dashboard.bookings.processingCancellation"));
      
      const result = await cancelBooking(bookingToCancel, classIdToCancel);
      
      if (result.success) {
        toast.success(t("dashboard.bookings.cancelSuccess"), {
          description: t("dashboard.bookings.cancelSuccessDesc"),
        });
        
        if (userId) {
          // Refresh the bookings list to show the updated state
          await fetchBookings(userId);
        }
      } else {
        console.error("[useBookings] Cancel booking error:", result.error);
        toast.error(t("dashboard.bookings.cancelError"), {
          description: result.error || t("dashboard.bookings.cancelErrorDesc"),
        });
      }
    } catch (error) {
      console.error("[useBookings] Error canceling booking:", error);
      toast.error(t("dashboard.bookings.cancelError"), {
        description: t("dashboard.bookings.unexpectedError"),
      });
    } finally {
      toast.dismiss();
      setIsCancelling(false);
      setBookingToCancel(null);
      setClassIdToCancel(null);
    }
  };
  
  const handleCancelDialogClose = () => {
    setBookingToCancel(null);
    setClassIdToCancel(null);
  };
  
  const currentBookings = bookings.filter(booking => !isBookingPast(booking));
  const pastBookings = bookings.filter(booking => isBookingPast(booking));
  
  return {
    bookings,
    currentBookings,
    pastBookings,
    isLoading,
    bookingToCancel,
    classIdToCancel,
    isCancelling,
    fetchBookings,
    isBookingPast,
    canCancelBooking,
    handleCancelBooking,
    handleConfirmCancel,
    handleCancelDialogClose
  };
};
